import type { Signal } from '@preact/signals-react'
import type { EventProperties } from '@segment/analytics-next'

import { analytics } from './segment'

let avoLoadedSignal: Signal<boolean | undefined> | undefined

/**
 * Sets the Avo loaded signal for tracking Avo Inspector initialization state.
 * @param signal - The signal to track Avo loaded state
 * @returns void
 */
export const setAvoLoadedSignal = (
  signal: Signal<boolean | undefined>,
): void => {
  avoLoadedSignal = signal
}

/**
 * Segment destination interface for Avo Inspector integration.
 * Provides methods for tracking events, identifying users, and logging pages.
 */
export interface SegmentDestination {
  /** Initializes the destination and marks Avo as loaded */
  make: () => void
  /** Logs a custom event to Segment. Returns Segment's track promise, so a caller can wait for it before a redirect. */
  logEvent: (
    eventName: string,
    eventProperties: EventProperties,
  ) => Promise<unknown>
  /** Sets user properties for a specific user */
  setUserProperties: (userId: string, userProperties: object) => void
  /** Identifies a user by their ID */
  identify: (userId: string) => void
  /** Unidentifies the current user */
  unidentify: () => void
  /** Logs a page view with optional properties */
  logPage: (pageName: string, eventProperties: EventProperties) => void
  /** Tracks a revenue event */
  revenue: (amount: number, eventProperties: EventProperties) => void
}

/**
 * Creates a Segment destination object for Avo Inspector integration.
 * This function is safe to call in SSR environments and will only execute browser-specific code when appropriate.
 * @returns SegmentDestination object with methods for tracking and user identification
 */
export const createSegmentDestination = (): SegmentDestination => {
  return {
    make: (): void => {
      if (typeof document === 'undefined') {
        return
      }

      // Set the avoLoaded signal if provided
      if (avoLoadedSignal) {
        avoLoadedSignal.value = true
      }
      document.dispatchEvent(new Event('avo_inspector_initialized'))
    },
    logEvent: (
      eventName: string,
      eventProperties: EventProperties,
    ): Promise<unknown> => analytics.track(eventName, eventProperties),
    setUserProperties: (userId: string, userProperties: object): void => {
      analytics.identify(userId, userProperties)
    },
    identify: (userId: string): void => {
      analytics.identify(userId)
    },
    unidentify: (): void => {
      analytics.identify(null)
    },
    logPage: (pageName: string, eventProperties: EventProperties): void => {
      const properties = { ...eventProperties, pageName }

      analytics.page(properties)
    },
    revenue: (amount: number, eventProperties: EventProperties): void => {
      const name = 'Purchase Complete'
      const properties = { ...eventProperties, revenue: amount }

      analytics.track(name, properties)
    },
  }
}

/**
 * The most calls a buffered destination holds before a host attaches. Later
 * calls are dropped, so a page with no host cannot grow the buffer unbounded.
 */
export const BUFFERED_DESTINATION_LIMIT = 100

/** A destination that holds calls until {@link BufferedSegmentDestination.attach}. */
export interface BufferedSegmentDestination extends SegmentDestination {
  /** Replays the buffered calls in order into `destination`, then forwards every later call to it. */
  attach: (destination: SegmentDestination) => void
}

/**
 * Creates a destination for a composed application's Avo client, so the
 * client can initialize before the host has loaded the transport. Each
 * buffered call keeps the arguments the generated client passed, so an event
 * keeps the system properties that were current when it fired.
 * @returns BufferedSegmentDestination that buffers until attached
 */
export const createBufferedDestination = (): BufferedSegmentDestination => {
  let target: SegmentDestination | undefined
  // `settle` resolves a buffered logEvent's promise with the real result on
  // replay, so a caller awaiting it before a redirect really waits for it.
  const buffer: Array<{
    method: keyof SegmentDestination
    args: unknown[]
    settle?: (result: unknown) => void
  }> = []

  const forward = (
    method: keyof SegmentDestination,
    args: unknown[],
  ): unknown =>
    (target?.[method] as (...callArgs: unknown[]) => unknown)(...args)

  const call = (method: keyof SegmentDestination, args: unknown[]): void => {
    if (target) {
      forward(method, args)
    } else if (buffer.length < BUFFERED_DESTINATION_LIMIT) {
      buffer.push({ method, args })
    }
  }

  return {
    make: (): void => {
      call('make', [])
    },
    logEvent: (eventName, eventProperties): Promise<unknown> => {
      if (target) {
        return target.logEvent(eventName, eventProperties)
      }
      if (buffer.length >= BUFFERED_DESTINATION_LIMIT) {
        return Promise.resolve(undefined)
      }
      return new Promise(settle => {
        buffer.push({
          method: 'logEvent',
          args: [eventName, eventProperties],
          settle,
        })
      })
    },
    setUserProperties: (userId, userProperties): void => {
      call('setUserProperties', [userId, userProperties])
    },
    identify: (userId): void => {
      call('identify', [userId])
    },
    unidentify: (): void => {
      call('unidentify', [])
    },
    logPage: (pageName, eventProperties): void => {
      call('logPage', [pageName, eventProperties])
    },
    revenue: (amount, eventProperties): void => {
      call('revenue', [amount, eventProperties])
    },
    attach: (destination): void => {
      if (target) {
        return
      }
      target = destination
      for (const { method, args, settle } of buffer.splice(0)) {
        const result = forward(method, args)
        settle?.(result)
      }
    },
  }
}

/**
 * Default Segment destination instance for backward compatibility.
 * Creates a new instance each time it's accessed. For new code, prefer using createSegmentDestination().
 * @deprecated Use createSegmentDestination() instead for better control over instance creation
 */
export const segmentDestination: SegmentDestination = createSegmentDestination()
